import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Crumb {
  label: string;
  href?: string;
}

interface BreadcrumbContextType {
  /** Page-published crumbs that override the nav-derived topbar trail, or null. */
  crumbs: Crumb[] | null;
  setCrumbs: (crumbs: Crumb[] | null) => void;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const BreadcrumbContext = createContext<BreadcrumbContextType | undefined>(undefined);

/**
 * Lets a page publish its own breadcrumb trail into the shared topbar breadcrumb
 * (see RootLayout's `Breadcrumbs`). Most pages derive their trail from the nav
 * structure automatically; pages whose trail can't be derived from the path
 * (e.g. a per-order sub-surface that needs the order number resolved from an id)
 * publish it here via `usePageBreadcrumbs`.
 */
export function BreadcrumbProvider({ children }: { children: ReactNode }): ReactNode {
  const [crumbs, setCrumbs] = useState<Crumb[] | null>(null);
  const value = useMemo<BreadcrumbContextType>(() => ({ crumbs, setCrumbs }), [crumbs]);
  return <BreadcrumbContext.Provider value={value}>{children}</BreadcrumbContext.Provider>;
}

export function useBreadcrumbContext(): BreadcrumbContextType {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbContext must be used within a BreadcrumbProvider');
  }
  return context;
}

/**
 * Publishes `crumbs` to the topbar breadcrumb for the lifetime of the calling
 * page, clearing them on unmount so the next page falls back to the nav-derived
 * trail. Pass `null` (or an empty array) to publish nothing.
 */
export function usePageBreadcrumbs(crumbs: Crumb[] | null): void {
  const { setCrumbs } = useBreadcrumbContext();
  // Serialise so a fresh array literal per render doesn't re-fire the effect.
  const serialized = crumbs && crumbs.length > 0 ? JSON.stringify(crumbs) : null;

  useEffect(() => {
    setCrumbs(serialized ? (JSON.parse(serialized) as Crumb[]) : null);
    return () => setCrumbs(null);
  }, [setCrumbs, serialized]);
}
